import type { TicketState } from './api.js';

/**
 * Whether a ticket's SLA clock has stopped for good.
 *
 * Every SLA display turns on this question, and for a long time none of them
 * asked it: the badge and the queue cell both computed `dueAt - now` and
 * reported the answer whatever state the ticket was in. Against the imported
 * FreshService corpus that put "Due now" on 47,792 tickets closed years ago,
 * inviting an agent to act on a countdown that had stopped.
 *
 * It lives here, rather than in either component, because the two have to
 * agree — a queue row saying "Due now" beside a detail page saying "Met" is
 * the same bug wearing a different label.
 */
export function isSettled(state: TicketState): boolean {
  return state === 'resolved' || state === 'closed' || state === 'cancelled';
}

export type SlaVerdict =
  /** Finished within the target. */
  | 'met'
  /** Finished after the target. */
  | 'missed'
  /** Finished, but there was no target to judge it against. */
  | 'no-target'
  /** Abandoned: neither met nor missed, because the work never happened. */
  | 'cancelled'
  /** Still running — the caller should show a countdown. */
  | 'live';

/**
 * The outcome of a ticket's resolution SLA.
 *
 * `cancelled` is deliberately not `met`. A ticket nobody was going to resolve
 * did not beat its target; claiming it did would invent a success out of
 * abandoned work and quietly flatter every report that counts met rates.
 */
export function slaVerdict(t: {
  state: TicketState;
  resolution_breached: boolean;
  resolution_due_at: string | null;
}): SlaVerdict {
  if (!isSettled(t.state)) return 'live';
  if (t.state === 'cancelled') return 'cancelled';
  if (t.resolution_breached) return 'missed';
  return t.resolution_due_at ? 'met' : 'no-target';
}

/** The fields the ring needs. Narrower than a full row, so the board can pass one too. */
export interface SlaRow {
  state: TicketState;
  resolution_breached: boolean;
  resolution_due_at: string | null;
  running_minutes: number;
}

/** Share of the resolution target consumed, for the ring. */
export function slaConsumed(t: SlaRow): number | null {
  if (t.resolution_breached) return 1;
  // A finished ticket's ring is full: the work is done, however long it
  // took. Leaving it to the arithmetic below drew a part-filled ring that
  // shrank every day as `now` moved further past a due date from 2020.
  if (isSettled(t.state)) return t.state === 'cancelled' ? null : 1;
  if (!t.resolution_due_at) return null;
  const remainingMs = new Date(t.resolution_due_at).getTime() - Date.now();
  const workedMs = t.running_minutes * 60_000;
  const total = workedMs + Math.max(0, remainingMs);
  if (total <= 0) return 1;
  return Math.max(0, Math.min(1, workedMs / total));
}
